export function countObjects(objectArray) {
    if (objectArray != null) {
        const count = Object.keys(objectArray).length;
        return count;
    } else return 0;
}

// One UUID per logical user action. Reuse across retries to dedup against
// the backend Idempotency-Key middleware; generate fresh on each new submit.
export function newIdempotencyKey() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// The master "ADMIN OVERRIDE" login (MAIN_USERNAME/MAIN_PASSWORD) has no real
// user record — its JWT id is this literal sentinel, which then lands on
// production_runs.operator_id and product.created_by. Surfacing it explicitly
// in producer/operator displays (instead of a blank or raw id) makes records
// produced via the override account easy to spot and clean up — e.g. after
// testing on a live customer tenant.
export const ADMIN_OVERRIDE_ID = "ADMIN OVERRIDE";
export const ADMIN_OVERRIDE_LABEL = "Admin override";

export function isAdminOverrideId(id) {
    return id === ADMIN_OVERRIDE_ID;
}

export function getDateStatus(timestamp) {
    if (!timestamp || timestamp === 0) return "unknown";
    const now = new Date();
    const date = new Date(timestamp);
    const daysUntil = (date - now) / (1000 * 60 * 60 * 24);
    if (daysUntil < 0) return "danger";
    if (daysUntil <= 3) return "warning";
    return "ok";
}

import countryData from "../json/iso_countries.json";

export function displayWeight(weightInGrams) {
    if (!weightInGrams) return "0 g";
    if (weightInGrams < 1000) {
        return `${weightInGrams} g`;
    } else if (weightInGrams < 1000000) {
        return `${(weightInGrams / 1000).toFixed(2)} kg`;
    } else {
        return `${(weightInGrams / 1000000).toFixed(2)} t`;
    }
}

export function batchID(id) {
    if (id == null) return "";
    return `${id.slice(0, 3).toUpperCase()}${id.slice(-3).toUpperCase()}`;
}

// productIntakeIds returns the full list of intakes a product consumed for a
// given ingredient. Prefers the multi-intake `intake_ids` field (set by the
// new ProductionRun allocator when a unit spans multiple intakes); falls back
// to legacy single `intake_id` for products created before the schema change.
export function productIntakeIds(ingredient) {
    if (!ingredient) return [];
    if (Array.isArray(ingredient.intake_ids) && ingredient.intake_ids.length > 0) {
        return ingredient.intake_ids;
    }
    return ingredient.intake_id ? [ingredient.intake_id] : [];
}

export function countryName(isoNumber) {
    if (isoNumber) {
        const selectedCountry = countryData.find(
            (country) => country["country-code"] === isoNumber
        );
        return selectedCountry?.name;
    }
    // Return an empty string if the born_in value is not defined
    return "";
}

// Currency support - inline config to avoid circular dependencies with store
const CURRENCY_CONFIG = {
    // Europe
    GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', name: 'British Pound' },
    EUR: { code: 'EUR', symbol: '€', locale: 'de-DE', name: 'Euro' },
    CHF: { code: 'CHF', symbol: 'CHF', locale: 'de-CH', name: 'Swiss Franc' },
    // Americas
    USD: { code: 'USD', symbol: '$', locale: 'en-US', name: 'US Dollar' },
    CAD: { code: 'CAD', symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
    // Asia-Pacific
    AUD: { code: 'AUD', symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' },
    NZD: { code: 'NZD', symbol: 'NZ$', locale: 'en-NZ', name: 'New Zealand Dollar' },
    SGD: { code: 'SGD', symbol: 'S$', locale: 'en-SG', name: 'Singapore Dollar' },
    HKD: { code: 'HKD', symbol: 'HK$', locale: 'en-HK', name: 'Hong Kong Dollar' },
    JPY: { code: 'JPY', symbol: '¥', locale: 'ja-JP', name: 'Japanese Yen', decimals: 0 },
    INR: { code: 'INR', symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
    // Africa
    TZS: { code: 'TZS', symbol: 'TSh', locale: 'sw-TZ', name: 'Tanzanian Shilling', decimals: 0 },
    ZAR: { code: 'ZAR', symbol: 'R', locale: 'en-ZA', name: 'South African Rand' },
    KES: { code: 'KES', symbol: 'KSh', locale: 'en-KE', name: 'Kenyan Shilling' },
    // Middle East
    AED: { code: 'AED', symbol: 'د.إ', locale: 'ar-AE', name: 'UAE Dirham' },
    SAR: { code: 'SAR', symbol: '﷼', locale: 'ar-SA', name: 'Saudi Riyal' },
};
const DEFAULT_CURRENCY = 'GBP';

// Lazy getter for store to avoid circular import issues
let _store = null;
function getStore() {
    if (!_store) {
        try {
            // Dynamic import to avoid circular dependency
            _store = require('@/store').store;
        } catch {
            return null;
        }
    }
    return _store;
}

function getCurrencyConfig(currencyCode = null) {
    if (currencyCode) return CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
    const store = getStore();
    const code = store?.state?.settings?.currency || DEFAULT_CURRENCY;
    return CURRENCY_CONFIG[code] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
}

export function getCurrencySymbol() {
    return getCurrencyConfig().symbol;
}

export function moneyString(valueInt, currencyCode = null) {
    if (!valueInt) {
        valueInt = "0.00";
    } else if (isNaN(parseInt(valueInt.toString().replace(/[^0-9]/g, "")))) {
        valueInt = "0.00";
    }
    const config = getCurrencyConfig(currencyCode);
    return (
        parseInt(
            valueInt
                .toString()
                .replace(/[^0-9]/g, "")
                .substring(0, 14) // Limits the max digits in the cost
        ) / 100
    ).toLocaleString(config.locale, { style: "currency", currency: config.code });
}

export function moneyInt(valueString) {
    if (!valueString) return 0;
    return parseInt(valueString.replace(/[^0-9]/g, ""));
}

export function validateWeight(weight, weightUnit) {
    if (weight == null) return "";

    try {
        if (weightUnit == "kg") {
            return weight.match(/([0-9]+[.][0-9]{0,3})|^[0-9]+/g)[0];
        } else if (weightUnit == "tonne") {
            return weight.match(/([0-9]+[.][0-9]{0,6})|^[0-9]+/g)[0];
        } else if (weightUnit == "g") {
            return weight.match(/^[0-9]+/g)[0];
        } else {
            return weight.match(/([0-9]+[.][0-9]*)|^[0-9]+/g)[0];
        }
    } catch {
        return "";
    }
}

export function validateDecimal(decimalNumber) {
    if (decimalNumber == null) return "";
    try {
        return decimalNumber.match(/([0-9]+[.][0-9]{0,2})|^[0-9]+/g)[0];
    } catch {
        return "";
    }
}

export function validateWholeNumber(wholeNumber) {
    if (wholeNumber == null) return "";
    if (wholeNumber == "") return "";
    try {
        return wholeNumber.match(/(?:[1-9][0-9]*[.][0-9]|[1-9][0-9]*)\b/g);
    } catch {
        return "";
    }
}

export function validateFormReference(reference) {
    if (reference == null) return "";
    return reference.toUpperCase().replace(/\s+/g, "-").replace(/-+/g, "-");
}

export function validatePin(pin) {
    if (pin == null) return "";
    try {
        return pin.match(/^[0-9]{1,4}/g)[0];
    } catch {
        return "";
    }
}

export function formatTimeDiff(timeDiff, maxUnits = 4) {
    // Return "1s" immediately if timeDiff is less than 1000 milliseconds
    if (timeDiff < 1000) return "1s";

    const units = [86400000, 3600000, 60000, 1000]; // Milliseconds per day, hour, minute, and second
    const labels = ["d", "h", "m", "s"];
    const parts = [];
    units.forEach((unit, index) => {
        const value = Math.floor(timeDiff / unit);
        timeDiff %= unit; // Update timeDiff for the next smaller unit
        if (value > 0) parts.push(`${value}${labels[index]}`);
    });

    // A capped diff is for glanceable countdowns ("1d 16h"), where trailing
    // seconds are false precision — keep them only when they're all there is.
    let shown = parts;
    if (maxUnits < labels.length && parts.length > 1) {
        shown = parts.filter((part) => !part.endsWith("s"));
    }
    return shown.slice(0, maxUnits).join(" ");
}

export function validateUsername(username) {
    if (username == null || username == "") return "";
    if (!username.toLowerCase().match(/^[a-z0-9]+/g)) return "";
    return username.toLowerCase().match(/^[a-z0-9]+/g)[0];
}

export function hasTag(item, tag) {
    for (let index = 0; index < item?.tags?.length; index++) {
        if (item.tags[index] == tag) return true;
    }
    return false;
}

export function isNoWeightStock(item) {
    if (item == null) return false;
    if (item.unit === "count") return true;
    if (hasTag(item, "PACKAGING_STOCK")) return true;
    return false;
}

export function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function filteredItemsByTag(items, tag) {
    if (!items) return [];
    const filteredItems = items?.filter((item) => {
        return hasTag(item, tag);
    });
    return filteredItems;
}

export function updateValueFormatted(value, currencyCode = null) {
    const config = getCurrencyConfig(currencyCode);
    // Parse the input value as a number of pence/cents
    const valueInMinorUnits = parseFloat(value);

    // Check whether the input value is an empty string
    if (value === "" || isNaN(valueInMinorUnits)) {
        return `${config.symbol}0.00`;
    }
    // Check that the input value is a number
    else if (!isNaN(valueInMinorUnits)) {
        // Convert the input value to major and minor units
        const majorUnits = Math.floor(valueInMinorUnits / 100);
        const minorUnits = valueInMinorUnits % 100;

        // Format the major and minor parts separately
        const majorFormatted = majorUnits.toLocaleString();
        const minorFormatted = minorUnits.toString().padStart(2, "0");

        // Combine the parts to form the formatted value
        const formattedValue = `${config.symbol}${majorFormatted}.${minorFormatted}`;

        // Return the formatted value
        return formattedValue;
    }
}

export function printTimeDDMMYYYY(timestamp) {
    if (timestamp == 0) return "Unknown";
    var date = new Date(timestamp);
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    });
}

export function printTimeShort(timestamp) {
    if (timestamp == null || timestamp == 0) return "Unknown";
    var date = new Date(timestamp);
    return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
    });
}

export function printTimeLong(timestamp) {
    if (timestamp == 0) return "Unknown";
    var date = new Date(timestamp);
    return date.toLocaleString("en-GB", {
        day: "numeric",
        weekday: "long",
        month: "long",
        year: "numeric"
    });
}

export function printTimeForTemperatureLogs(timestamp) {
    if (timestamp == 0) return "Unknown";
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if ((hours === 0 && minutes === 0) || (hours === 12 && minutes === 0)) {
        return printTimeAMPM(timestamp);
    } else {
        return printTime24Long(timestamp);
    }
}

export function printTime24Long(timestamp) {
    const time = new Date(timestamp);
    const timeAMPM = time.toLocaleString("en-GB", {
        hour: "numeric",
        minute: "numeric"
    });
    return timeAMPM;
}

export function printTimeAMPM(timestamp) {
    const time = new Date(timestamp);
    const timeAMPM = time.toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true
    });
    return timeAMPM.slice(-2);
}

export function convertUnixTimestampToDateTime(timestamp) {
    const date = new Date(timestamp);
    return date.toISOString().split(".")[0];
}

export function displayDateTime(timestamp) {
    if (!timestamp || timestamp === 0) return "N/A";
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
    });
}

export const printTimeAgo = (time) => {
    const created = new Date(time);
    const now = new Date();
    const diff = now.getTime() - created.getTime();

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 0) {
        return `${days} day(s) ago`;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 0) {
        return `${hours} hour(s) ago`;
    }

    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes > 0) {
        return `${minutes} minute(s) ago`;
    }

    return `just now`;
};

export function printDateBestBy(timestamp) {
    if (timestamp == 0) return "Unknown";
    var date = new Date(timestamp);
    return (
        (date.getDate() > 9 ? date.getDate() : "0" + date.getDate()) +
        "/" +
        (date.getMonth() > 8 ? date.getMonth() + 1 : "0" + (date.getMonth() + 1)) +
        "/" +
        date.getFullYear()
    );
}

export function convertTimestampToDate(timestamp) {
    var date = new Date(timestamp);
    var year = date.getFullYear();
    var month = ("0" + (date.getMonth() + 1)).slice(-2);
    var day = ("0" + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
}

export function timeConverter(timestamp) {
    if (timestamp == 0) return "Unknown";

    var a = new Date(timestamp);
    var months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
    ];
    var year = a.getFullYear();
    var month = months[a.getMonth()];
    var date = a.getDate();
    var hour = a.getHours();
    var min = a.getMinutes();
    var sec = a.getSeconds();
    var time =
        date +
        " " +
        month +
        " " +
        year +
        " " +
        ("0" + hour).slice(-2) +
        ":" +
        ("0" + min).slice(-2) +
        ":" +
        ("0" + sec).slice(-2);
    return time;
}

export function checkFormItemID(title) {
    if (title == null) return "";
    return title.replace(/[^0-9A-Z]+/gi, "").toLowerCase();
}

export function truncateString(str, num) {
    if (str.length <= num) {
        return str;
    }
    return str.slice(0, num) + "...";
}

export function toast(toastMessage, toastTimeout, type) {
    let instance = this.$toast.open({
        message: toastMessage,
        type: type ? type : "success"
    });

    setTimeout(
        () => {
            instance.dismiss();
        },
        toastTimeout ? toastTimeout * 1000 : 3000
    );
}

export function displayTitleCase(text) {
    if (text == null) return "";
    if (text === "") return "";
    return text
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export async function copyToClipboard(mytext, { toast = this?.$toast, label = "ID" } = {}) {
    if (mytext == null || mytext === "") return false;
    try {
        await navigator.clipboard.writeText(String(mytext));
    } catch {
        toast?.error?.(`${label} could not be copied. Try again.`);
        return false;
    }
    toast?.success?.(`${label} copied`);
    return true;
}

import cronstrue from "cronstrue";
import { parseScheduleCron } from "@/composables/checkScheduleCron";

export function cronDescription(cronString) {
    if (cronString === undefined || cronString == null) return "Unknown";
    try {
        const once = parseScheduleCron(cronString);
        if (once?.scheduleType === "once") {
            const date = new Intl.DateTimeFormat("en-GB", {
                timeZone: "UTC",
                dateStyle: "medium"
            }).format(new Date(`${once.date}T00:00:00Z`));
            return `Once on ${date} at ${once.time}`;
        }
        return cronstrue.toString(cronString);
    } catch {
        return "";
    }
}

// intakeFefoCompare orders production intakes the same way the backend
// allocator consumes them: in-date before expired, then FEFO (soonest use-by
// first), undated stock (no use-by, e.g. packaging) after dated, and oldest
// booked-in as a stable tie-break. Operates on the production-context intake
// shape (`expired`, `useby_date`, `intake_time`).
export function intakeFefoCompare(a, b) {
    const ae = a.expired ? 1 : 0;
    const be = b.expired ? 1 : 0;
    if (ae !== be) return ae - be;
    const aDated = (a.useby_date || 0) > 0;
    const bDated = (b.useby_date || 0) > 0;
    if (aDated !== bDated) return aDated ? -1 : 1;
    if (aDated && a.useby_date !== b.useby_date) return a.useby_date - b.useby_date;
    return (a.intake_time || 0) - (b.intake_time || 0);
}
